import { component } from 'picoapp';
import Modal from '~/js/utils/modal';

/**
 * Option drawer tab functionality
 */
class OptionDrawerTabs {
  constructor(drawer) {
    this.drawer = drawer;
    this.tabTriggers = drawer.querySelectorAll('[data-tab-trigger]');
    this.tabContents = drawer.querySelectorAll('[data-tab-content]');

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = trigger.getAttribute('data-tab-trigger');
        this.switchTab(targetTab, trigger);
      });
    });

    // Handle add to cart for ring measurement product
    const addToCartBtn = this.drawer.querySelector('[data-ring-measurement-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        this.handleAddToCart(e);
      });
    }

    // Handle reserve button
    const reserveBtn = this.drawer.querySelector('[data-ring-measurement-reserve]');
    if (reserveBtn) {
      reserveBtn.addEventListener('click', (e) => {
        this.handleReservation(e);
      });
    }
  }

  switchTab(targetTab, trigger) {
    // Update tab triggers
    this.tabTriggers.forEach((btn) => {
      btn.classList.remove('bg-warm-gray-100', 'text-black');
      btn.classList.add('bg-white', 'text-warm-gray-500');
      btn.setAttribute('aria-selected', 'false');
    });

    trigger.classList.remove('bg-white', 'text-warm-gray-500');
    trigger.classList.add('bg-warm-gray-100', 'text-black');
    trigger.setAttribute('aria-selected', 'true');

    // Update tab contents
    this.tabContents.forEach((content) => {
      content.classList.remove('active');
    });

    const targetContent = this.drawer.querySelector(`[data-tab-content="${targetTab}"]`);
    if (targetContent) {
      targetContent.classList.add('active');
    }
  }

  async handleAddToCart(e) {
    e.preventDefault();
    const button = e.target;
    const variantId = button.getAttribute('data-variant-id');

    if (!variantId) return;

    button.disabled = true;
    const originalText = button.innerHTML;
    const addingText = button.getAttribute('data-adding-text') || 'Tilføjer...';
    const addedText = button.getAttribute('data-added-text') || 'Tilføjet!';

    button.innerHTML = addingText;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (response.ok) {
        // Update button text temporarily
        button.innerHTML = addedText;

        // Close the option drawer first, then trigger cart events
        const optionDrawerCloseBtn = this.drawer.querySelector('[data-action="close"]');
        if (optionDrawerCloseBtn) {
          optionDrawerCloseBtn.click();
        }

        // Small delay to ensure option drawer closes before cart drawer opens
        setTimeout(() => {
          // Trigger cart product added event (same as main product form)
          document.dispatchEvent(new CustomEvent('cart:product:added'));
        }, 150);

        setTimeout(() => {
          button.innerHTML = originalText;
          button.disabled = false;
        }, 4000);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      // Error handled silently
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  handleReservation(e) {
    e.preventDefault();
    // Find the product ID from the drawer's data attributes or ring measurement product
    const productId = this.drawer.dataset.productId
      || this.drawer.querySelector('[data-ring-measurement-cart]')?.dataset.productId;
    if (productId) {
      // Trigger the product-specific click and collect drawer
      const ccButton = document.querySelector(`[data-drawer-trigger="cc-drawer-${productId}"]`);
      if (ccButton) {
        ccButton.click();
      }
    }
  }
}

export default component(async (node) => {
  const { key } = node.dataset;
  const optionModal = Modal(node, false, key);
  const modalClosers = [...node.querySelectorAll('[data-action="close"]')];

  // Initialize tab functionality
  const tabs = new OptionDrawerTabs(node); // eslint-disable-line no-unused-vars

  // Listen to modal-triggers via delegation so dynamically moved buttons are covered
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(`[data-modal-trigger="${key}"]`);
    if (trigger) {
      e.preventDefault();
      optionModal.toggle();
    }
  });

  // Listen to modal-closers
  if (modalClosers.length) {
    modalClosers.forEach((close) => {
      close.addEventListener('click', (e) => {
        e.preventDefault();
        optionModal.close();
      });
    });
  }

  const overlayElement = document.querySelector('#option-drawer-overlay');

  if (overlayElement) {
    optionModal.on('open', () => {
      overlayElement.classList.add('overlay--active');
    });

    optionModal.on('close', () => {
      overlayElement.classList.remove('overlay--active');
    });
  }
});
